import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Space, Table, Tag, Typography, Modal, Descriptions, Tooltip, Form, Input, Checkbox, Grid, Popconfirm } from "antd";
import { EyeOutlined, PlusOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const AgentList = () => {
  const screens = useBreakpoint();
  const { request } = useRequest();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [phoneInputValue, setPhoneInputValue] = useState("91");
  const [phoneDialCode, setPhoneDialCode] = useState("91");
  const searchTimerRef = useRef(null);
  const [editForm] = Form.useForm();
  const authRole = localStorage.getItem("authRole") || "admin";
  const isAdminViewOnly = authRole === "admin";
  const isHotelPanel = authRole === "hotel";

  const getManagerId = () => {
    try {
      const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      const loginData = JSON.parse(localStorage.getItem("loginData") || "{}");
      return (
        profile?.Hotel_Id ||
        loginData?.managerDetails?.Hotel_Id ||
        null
      );
    } catch (_err) {
      return null;
    }
  };

  const normalizeToArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload?.data && Array.isArray(payload.data)) return payload.data;
    if (payload?.Hotels_details && Array.isArray(payload.Hotels_details)) return payload.Hotels_details;
    if (payload?.Details && Array.isArray(payload.Details)) return payload.Details;
    return [];
  };

  const fetchAgents = (search = "") => {
    setLoading(true);
    const trimmed = search.trim();
    const url = trimmed
      ? `${apiPath.baseURL}/all_agents?search=${encodeURIComponent(trimmed)}`
      : `${apiPath.baseURL}/all_agents`;

    request({
      url,
      method: "GET",
      onSuccess: (res) => {
        setData(normalizeToArray(res));
        setLoading(false);
      },
      onError: () => {
        setData([]);
        setLoading(false);
      },
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchAgents(value);
    }, 500);
  };

  const handleSearchSubmit = (value) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    fetchAgents(value);
  };

  const fetchHotels = () => {
    const managerId = getManagerId();
    const url =
      authRole === "hotel" && managerId
        ? `${apiPath.baseURL}/get_Hotels_of_HotelPartner/${managerId}`
        : `${apiPath.baseURL}/getAllHotels`;

    request({
      url,
      method: "GET",
      onSuccess: (res) => {
        if (res?.success) {
          const hotelData =
            res.Hotels_details ||
            res.Hotel_Details ||
            res.hotel_Details ||
            res.data ||
            [];
          setHotels(Array.isArray(hotelData) ? hotelData : []);
        }
      },
      onError: () => {
        setHotels([]);
      },
    });
  };

  useEffect(() => {
    fetchAgents();
    fetchHotels();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const getHotelNameById = (hotelId) => {
    if (!hotelId) return "Unknown Hotel";
    const normalizedId = String(hotelId).trim();
    const hotel = hotels.find((h) => {
      const candidates = [h?.Hotel_Id, h?._id, h?.id]
        .filter(Boolean)
        .map((value) => String(value).trim());
      return candidates.includes(normalizedId);
    });
    return hotel?.Hotel_name || hotel?.name || "Unknown Hotel";
  };

  const renderHotelTags = (hotelsInput = [], options = {}) => {
    const { maxVisible = 2, dense = false, scroll = false, maxHeight = 120 } = options;
    if (!Array.isArray(hotelsInput) || hotelsInput.length === 0) {
      return <span style={{ color: "#999" }}>-</span>;
    }

    const normalizedHotels = hotelsInput
      .map((item) => {
        if (item && typeof item === "object") {
          return {
            id: item.id || item.Hotel_Id || item._id || item.value || "",
            name: item.name || item.Hotel_name || "",
          };
        }
        return {
          id: item,
          name: getHotelNameById(item),
        };
      })
      .filter((hotel) => hotel.id || hotel.name);

    if (normalizedHotels.length === 0) {
      return <span style={{ color: "#999" }}>-</span>;
    }

    const visibleHotels = normalizedHotels.slice(0, maxVisible);
    const remainingHotels = normalizedHotels.slice(maxVisible);

    const content = (
      <div style={{ display: "flex", flexWrap: "wrap", gap: dense ? 4 : 6 }}>
        {visibleHotels.map((hotel, index) => {
          const hotelName = hotel.name || getHotelNameById(hotel.id);
          return (
            <Tooltip key={`${hotel.id || hotelName}-${index}`} title={hotelName}>
              <Tag
                color="orange"
                style={{
                  margin: 0,
                  maxWidth: dense ? 110 : 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {hotelName}
              </Tag>
            </Tooltip>
          );
        })}

        {remainingHotels.length > 0 && (
          <Tooltip
            title={
              <div style={{ maxWidth: 260 }}>
                {remainingHotels.map((hotel, index) => (
                  <div
                    key={`${hotel.id || hotel.name}-more-${index}`}
                    style={{ marginBottom: index === remainingHotels.length - 1 ? 0 : 4 }}
                  >
                    {hotel.name || getHotelNameById(hotel.id)}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="blue" style={{ margin: 0, cursor: "pointer" }}>
              +{remainingHotels.length} more
            </Tag>
          </Tooltip>
        )}
      </div>
    );

    if (scroll) {
      return (
        <div style={{ maxHeight, overflowY: "auto", paddingRight: 4 }}>
          {content}
        </div>
      );
    }

    return content;
  };

  const handleViewDetails = (agent) => {
    setSelectedAgent(agent);
    setDetailModalVisible(true);
  };

  const handleEditAgent = (agent) => {
    if (isAdminViewOnly) return;
    setSelectedAgent(agent);
    setSelectedHotels(agent.associated_hotel_ids || []);

    // Derive dial code digits (e.g. "91") from contact_country_code ("+91" or "91")
    const rawCode = String(agent.contact_country_code || "91").replace("+", "");
    setPhoneDialCode(rawCode);

    // Build the raw phone digits that PhoneInput expects: dialCode + number
    let rawNumber = String(agent.contact_no || agent.phone_no || "").trim();
    // Strip leading + or country code if the number was stored combined
    rawNumber = rawNumber.replace(/^\+/, "");
    if (rawNumber.startsWith(rawCode)) {
      rawNumber = rawNumber.slice(rawCode.length).trim();
    }
    rawNumber = rawNumber.replace(/\D/g, ""); // digits only
    setPhoneInputValue(rawCode + rawNumber);

    editForm.setFieldsValue({
      name: agent.name || agent.agent_name,
      email: agent.email,
      contact_no: `+${rawCode} ${rawNumber}`,
      notes: agent.notes || ""
    });
    setEditModalVisible(true);
  };

  const handleHotelChangeForEdit = (checkedValues) => {
    setSelectedHotels(checkedValues);
  };

  const handleUpdateAgent = (values) => {
    if (isAdminViewOnly) return;
    setEditLoading(true);
    const managerId = getManagerId();
    const updateUrl =
      authRole === "hotel" && managerId
        ? `${apiPath.baseURL}/manager/update_agent/${managerId}/${selectedAgent.agent_id}`
        : `${apiPath.baseURL}/admin/update_agent/${selectedAgent.agent_id}`;

    const numberOnly = phoneInputValue.startsWith(phoneDialCode)
      ? phoneInputValue.slice(phoneDialCode.length)
      : phoneInputValue;

    const payload = {
      name: values.name,
      email: values.email,
      contact_no: `+${phoneDialCode} ${numberOnly}`,
      contact_country_code: `+${phoneDialCode}`,
      notes: values.notes || ""
    };
    if (!isHotelPanel) {
      payload.associated_hotel_ids = selectedHotels;
    }

    // Only include password if it's provided and not empty
    if (values.password && values.password.trim() !== "") {
      payload.password = values.password;
    }

    console.log("Update payload:", payload); // Debug log

    request({
      url: updateUrl,
      method: "PUT",
      data: payload,
      onSuccess: (res) => {
        if (res?.success) {
          ShowToast("Agent updated successfully", Severty.SUCCESS);
          setEditModalVisible(false);
          fetchAgents(); // Refresh the list
        } else {
          ShowToast(res?.message || "Failed to update agent", Severty.ERROR);
        }
        setEditLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to update agent", Severty.ERROR);
        setEditLoading(false);
      },
    });
  };

  const handleToggleStatus = (agent) => {
    const managerId = getManagerId();
    if (!managerId) {
      ShowToast("Hotel ID not found", Severty.ERROR);
      return;
    }
    const newStatus = agent.status === 1 ? 0 : 1;
    request({
      url: apiPath.toggleAgentStatus(managerId, agent.agent_id),
      method: "POST",
      data: { status: newStatus },
      onSuccess: (res) => {
        if (res?.success) {
          ShowToast(
            `Agent ${newStatus === 1 ? "activated" : "deactivated"} successfully`,
            Severty.SUCCESS
          );
          setData((prev) =>
            prev.map((a) =>
              a.agent_id === agent.agent_id ? { ...a, status: newStatus } : a
            )
          );
        } else {
          ShowToast(res?.message || "Failed to update status", Severty.ERROR);
        }
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to update status", Severty.ERROR);
      },
    });
  };

  const columns = [
    {
      title: "Agent ID",
      dataIndex: "agent_id",
      key: "agent_id",
      render: (text) => <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{text}</span>
    },
    {
      title: "Agent Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text}</strong>
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => <span style={{ color: '#1890ff' }}>{text}</span>
    },
    {
      title: "Phone",
      key: "phone",
      render: (_, record) => {
        return `${record.contact_country_code} ${record.contact_no}`;
      }
    },
    ...(!isHotelPanel ? [{
      title: "Associated Hotels",
      dataIndex: "associated_hotel_ids",
      key: "associated_hotel_ids",
      width: 260,
      render: (_, record) =>
        renderHotelTags(
          record?.associated_hotels?.length
            ? record.associated_hotels
            : record?.associated_hotel_ids || [],
          { maxVisible: 2, dense: true }
        ),
    }] : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const tag = (
          <Tag
            color={status === 1 ? "green" : "red"}
            style={isHotelPanel ? { cursor: "pointer" } : {}}
          >
            {status === 1 ? "Active" : "Inactive"}
          </Tag>
        );
        if (!isHotelPanel) return tag;
        return (
          <Popconfirm
            title={`${status === 1 ? "Deactivate" : "Activate"} Agent`}
            description={`Are you sure you want to ${status === 1 ? "deactivate" : "activate"} this agent?`}
            onConfirm={() => handleToggleStatus(record)}
            okText="Yes"
            cancelText="No"
          >
            {tag}
          </Popconfirm>
        );
      },
    },
    {
      title: "Verified",
      dataIndex: "is_verified",
      key: "is_verified",
      render: (isVerified) => (
        <Tag color={isVerified ? 'green' : 'orange'}>
          {isVerified ? 'Verified' : 'Not Verified'}
        </Tag>
      )
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{new Date(createdAt).toLocaleString()}</span>
      )
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {!isAdminViewOnly && (
            <Tooltip title="Edit Agent">
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditAgent(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={4} style={{ margin: 0 }}>Agent Management</Title>
        <Space wrap>
          <Input.Search
            placeholder="Search by Agent ID, Name, Email or Phone"
            allowClear
            value={searchValue}
            onChange={handleSearchChange}
            onSearch={handleSearchSubmit}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            style={{ width: screens.xs ? '100%' : 320 }}
          />
          {!isAdminViewOnly && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/agent-management/add')}
            >
              Add Agent
            </Button>
          )}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        scroll={{ x: 1000 }}
        rowKey="_id"
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />

      <Modal
        title="Agent Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={screens.xs ? "95%" : 800}
        centered
        bodyStyle={{ padding: screens.xs ? 12 : 24 }}
      >
        {selectedAgent && (
          <Descriptions
            bordered
            column={screens.xs ? 1 : 2}
            size={screens.xs ? "small" : "default"}
            labelStyle={{ width: screens.xs ? 110 : "auto" }}
          >
            <Descriptions.Item label="Agent ID" span={screens.xs ? 1 : 2}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: "break-all" }}>
                {selectedAgent.agent_id}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Agent Name">
              <span style={{ wordBreak: "break-word" }}>{selectedAgent.name}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              <span style={{ color: '#1890ff', wordBreak: "break-all" }}>{selectedAgent.email}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              <span style={{ wordBreak: "break-all" }}>{selectedAgent.contact_no}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedAgent.status === 1 ? 'green' : 'red'}>
                {selectedAgent.status === 1 ? 'Active' : 'Inactive'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Verified">
              <Tag color={selectedAgent.is_verified ? 'green' : 'orange'}>
                {selectedAgent.is_verified ? 'Verified' : 'Not Verified'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Created By">
              {selectedAgent.created_by}
            </Descriptions.Item>
            {!isHotelPanel && (
              <Descriptions.Item label="Associated Hotels" span={2}>
                {renderHotelTags(
                  selectedAgent?.associated_hotels?.length
                    ? selectedAgent.associated_hotels
                    : selectedAgent?.associated_hotel_ids || [],
                  {
                    maxVisible:
                      selectedAgent?.associated_hotels?.length ||
                      selectedAgent?.associated_hotel_ids?.length ||
                      0,
                    scroll: true,
                    maxHeight: 120,
                  }
                )}
              </Descriptions.Item>
            )}
            {selectedAgent.notes && (
              <Descriptions.Item label="Notes" span={2}>
                {selectedAgent.notes}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Created At">
              {new Date(selectedAgent.createdAt).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {new Date(selectedAgent.updatedAt).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Edit Agent Modal */}
      <Modal
        title="Edit Agent"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateAgent}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              label="Agent Name"
              name="name"
            >
              <Input placeholder="Enter agent name" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="contact_no"
              rules={[
                { required: true, message: 'Please enter a phone number' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const cleaned = value.replace(/[^\d+]/g, "");
                    if (!/^\+[1-9]\d{6,14}$/.test(cleaned)) {
                      return Promise.reject("Enter a valid phone number with country code");
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <PhoneInput
                country="in"
                value={phoneInputValue}
                onChange={(phone, country) => {
                  const dialCode = country?.dialCode || "91";
                  setPhoneDialCode(dialCode);
                  setPhoneInputValue(phone);
                  const numberOnly = phone.startsWith(dialCode) ? phone.slice(dialCode.length) : phone;
                  editForm.setFieldsValue({ contact_no: `+${dialCode} ${numberOnly}` });
                }}
                inputStyle={{ width: "100%", fontSize: "15px" }}
                dropdownStyle={{ fontSize: "14px" }}
                placeholder="Enter phone number"
                enableSearch
                disableSearchIcon={false}
              />
            </Form.Item>

            <Form.Item
              label="Notes"
              name="notes"
              style={{ gridColumn: '1 / -1' }}
            >
              <Input.TextArea
                placeholder="Enter any additional notes (optional)"
                rows={4}
                style={{ resize: 'none' }}
              />
            </Form.Item>
          </div>

          {!isHotelPanel && (
            <Form.Item
              label="Associated Hotels"
              style={{ marginTop: 16 }}
            >
              <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  <Checkbox.Group
                    style={{ width: '100%' }}
                    onChange={handleHotelChangeForEdit}
                    value={selectedHotels}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {hotels.map((hotel) => (
                        <div key={hotel._id} style={{ marginBottom: 8 }}>
                          <Checkbox value={hotel.Hotel_Id}>
                            <div>
                              <strong>{hotel.Hotel_name}</strong>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                ID: {hotel.Hotel_Id} | {hotel.city}
                              </div>
                            </div>
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </Checkbox.Group>
                </div>
                {selectedHotels.length > 0 && (
                  <div style={{ marginTop: 12, padding: 8, backgroundColor: '#e6f7ff', borderRadius: 4 }}>
                    <strong>Selected Hotels ({selectedHotels.length}):</strong>
                    <div style={{ marginTop: 4 }}>
                      {selectedHotels.map(hotelId => {
                        const hotel = hotels.find(h => h.Hotel_Id === hotelId);
                        return hotel ? (
                          <span key={hotelId} style={{
                            display: 'inline-block',
                            margin: '2px 4px 2px 0',
                            padding: '2px 8px',
                            backgroundColor: '#1890ff',
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            {hotel.Hotel_name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </Card>
            </Form.Item>
          )}

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={editLoading}
              >
                Update Agent
              </Button>
              <Button onClick={() => setEditModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AgentList;
